import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { apiFetch } from "./client";
import type {
  ConnectChesscomRequest,
  CreateTrainingSessionRequest,
  CursorPage,
  DeleteWeekOut,
  GameOut,
  LichessStartResponse,
  MetricSeriesOut,
  RoutineIn,
  RoutineOut,
  RunHandle,
  SolveItemRequest,
  StatsOut,
  SummaryOut,
  TrainingSessionOut,
  UserOut,
  UserUpdate,
  WeeklyStatsOut,
} from "./types";

export const QK = {
  me: ["me"] as const,
  games: (limit: number, cursor: string | null) => ["games", limit, cursor] as const,
  game: (id: string) => ["game", id] as const,
  trainingSession: (id: string) => ["training", id] as const,
  routines: ["routines"] as const,
  adminMetric: (key: string, start: string, end: string) =>
    ["admin", "metric", key, start, end] as const,
  adminSummary: (key: string) => ["admin", "summary", key] as const,
  adminWeekly: ["admin", "weekly"] as const,
};

export function useStats() {
  return useQuery<StatsOut, Error>({
    queryKey: ["stats"],
    queryFn: () => apiFetch<StatsOut>("/v1/stats"),
    staleTime: 30_000,
  });
}

export function useMe(opts?: Omit<UseQueryOptions<UserOut, Error>, "queryKey" | "queryFn">) {
  return useQuery<UserOut, Error>({
    queryKey: QK.me,
    queryFn: () => apiFetch<UserOut>("/v1/me"),
    staleTime: 30_000,
    retry: false,
    ...opts,
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation<UserOut, Error, UserUpdate>({
    mutationFn: (body) =>
      apiFetch<UserOut>("/v1/me", { method: "PATCH", body: JSON.stringify(body) }),
    onSuccess: (data) => qc.setQueryData(QK.me, data),
  });
}

export function useCreateSession() {
  const qc = useQueryClient();
  return useMutation<UserOut, Error, void>({
    mutationFn: () => apiFetch<UserOut>("/v1/auth/session", { method: "POST", body: "{}" }),
    onSuccess: (data) => qc.setQueryData(QK.me, data),
  });
}

export function useGames(limit = 20, cursor: string | null = null) {
  return useQuery<CursorPage<GameOut>, Error>({
    queryKey: QK.games(limit, cursor),
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(limit) });
      if (cursor) q.set("cursor", cursor);
      return apiFetch<CursorPage<GameOut>>(`/v1/games?${q}`);
    },
  });
}

// All games (every field), unpaginated — the profile view sorts/filters these
// entirely client-side.
export function useAllGames() {
  return useQuery<GameOut[], Error>({
    queryKey: ["games", "all"],
    queryFn: () => apiFetch<GameOut[]>("/v1/games/all"),
    staleTime: 30_000,
  });
}

export function useInfiniteGames(pageSize = 50) {
  return useInfiniteQuery<CursorPage<GameOut>, Error>({
    queryKey: ["games", "infinite", pageSize],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const q = new URLSearchParams({ limit: String(pageSize) });
      if (pageParam) q.set("cursor", pageParam as string);
      return apiFetch<CursorPage<GameOut>>(`/v1/games?${q}`);
    },
    getNextPageParam: (last) => last.next_cursor,
  });
}

export function useGame(gameId: string | null) {
  return useQuery<GameOut, Error>({
    queryKey: QK.game(gameId ?? ""),
    queryFn: () => apiFetch<GameOut>(`/v1/games/${gameId}`),
    enabled: !!gameId,
  });
}


export function useTrainingSession(sessionId: string | null) {
  return useQuery<TrainingSessionOut, Error>({
    queryKey: QK.trainingSession(sessionId ?? ""),
    queryFn: () => apiFetch<TrainingSessionOut>(`/v1/training/sessions/${sessionId}`),
    enabled: !!sessionId,
  });
}

export function useCreateTrainingSession() {
  return useMutation<TrainingSessionOut, Error, CreateTrainingSessionRequest>({
    mutationFn: (body) =>
      apiFetch<TrainingSessionOut>("/v1/training/sessions", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

// One-position session for a specific mistake (analysis "Solve").
export function useSolveMistake() {
  return useMutation<TrainingSessionOut, Error, string>({
    mutationFn: (mistakeId) =>
      apiFetch<TrainingSessionOut>(`/v1/training/sessions/from-mistake/${mistakeId}`, {
        method: "POST",
      }),
  });
}

// Session from an explicit list of mistakes (analysis "Fix all mistakes").
export function useFixAllMistakes() {
  return useMutation<TrainingSessionOut, Error, string[]>({
    mutationFn: (mistakeIds) =>
      apiFetch<TrainingSessionOut>("/v1/training/sessions/from-mistakes", {
        method: "POST",
        body: JSON.stringify({ mistake_ids: mistakeIds }),
      }),
  });
}

// Endless training: a random batch of unresolved mistakes.
export function useCreateEndless() {
  return useMutation<TrainingSessionOut, Error, void>({
    mutationFn: () =>
      apiFetch<TrainingSessionOut>("/v1/training/sessions/endless", { method: "POST" }),
  });
}

// Endless refill: another random batch (a new session) to keep going.
export function useRefillEndless() {
  return useMutation<TrainingSessionOut, Error, string>({
    mutationFn: (sessionId) =>
      apiFetch<TrainingSessionOut>(`/v1/training/sessions/${sessionId}/refill`, {
        method: "POST",
      }),
  });
}

export function useMarkItemSolved() {
  const qc = useQueryClient();
  return useMutation<void, Error, { itemId: string; outcome?: Partial<SolveItemRequest> }>({
    mutationFn: ({ itemId, outcome }) =>
      apiFetch<void>(`/v1/training/sessions/items/${itemId}/solve`, {
        method: "POST",
        body: JSON.stringify(outcome ?? { solved: true }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["stats"] });
    },
  });
}

// Mark a whole session finished — rolls up the routine occurrence (streaks).
export function useCompleteSession() {
  return useMutation<void, Error, string>({
    mutationFn: (sessionId) =>
      apiFetch<void>(`/v1/training/sessions/${sessionId}/complete`, { method: "POST" }),
  });
}

// ---- routines ----

export function useRoutines() {
  return useQuery<RoutineOut[], Error>({
    queryKey: QK.routines,
    queryFn: () => apiFetch<RoutineOut[]>("/v1/training/routines"),
  });
}

export function useCreateRoutine() {
  const qc = useQueryClient();
  return useMutation<RoutineOut, Error, RoutineIn>({
    mutationFn: (body) =>
      apiFetch<RoutineOut>("/v1/training/routines", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QK.routines }),
  });
}

export function useUpdateRoutine() {
  const qc = useQueryClient();
  return useMutation<RoutineOut, Error, { id: string; body: RoutineIn }>({
    mutationFn: ({ id, body }) =>
      apiFetch<RoutineOut>(`/v1/training/routines/${id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QK.routines }),
  });
}

export function useDeleteRoutine() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/v1/training/routines/${id}`, { method: "DELETE" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: QK.routines }),
  });
}

export function useRunRoutine() {
  return useMutation<TrainingSessionOut, Error, string>({
    mutationFn: (id) =>
      apiFetch<TrainingSessionOut>(`/v1/training/routines/${id}/run`, { method: "POST" }),
  });
}

export function useConnectChesscom() {
  const qc = useQueryClient();
  return useMutation<RunHandle, Error, ConnectChesscomRequest>({
    mutationFn: (body) =>
      apiFetch<RunHandle>("/v1/connect/chesscom", { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.me }),
  });
}

export function useStartLichess() {
  return useMutation<LichessStartResponse, Error, "web" | "mobile" | void>({
    mutationFn: (platform) =>
      apiFetch<LichessStartResponse>("/v1/connect/lichess/start", {
        method: "POST",
        body: JSON.stringify({ platform: platform || "web" }),
      }),
  });
}

// Re-fetch ALL of the user's games (full backfill) to recover anything the
// incremental sync missed. Idempotent server-side.
export function useResync() {
  return useMutation<{ triggered: number; run_ids: string[] }, Error, void>({
    mutationFn: () => apiFetch("/v1/resync", { method: "POST" }),
  });
}

// ---- admin observability ----

// Charts poll every 5s; summaries move slowly so they poll every 30s.
export function useAdminMetric(key: string, start: string, end: string) {
  return useQuery<MetricSeriesOut, Error>({
    queryKey: QK.adminMetric(key, start, end),
    queryFn: () => {
      const q = new URLSearchParams({ start, end });
      return apiFetch<MetricSeriesOut>(`/v1/admin/metrics/${key}?${q}`);
    },
    refetchInterval: 5_000,
  });
}

export function useAdminSummary(key: string) {
  return useQuery<SummaryOut, Error>({
    queryKey: QK.adminSummary(key),
    queryFn: () => apiFetch<SummaryOut>(`/v1/admin/summary/${key}`),
    refetchInterval: 30_000,
  });
}

export function useAdminWeekly() {
  return useQuery<WeeklyStatsOut, Error>({
    queryKey: QK.adminWeekly,
    queryFn: () => apiFetch<WeeklyStatsOut>("/v1/admin/data/weekly"),
  });
}

export function useDeleteWeek() {
  const qc = useQueryClient();
  return useMutation<DeleteWeekOut, Error, { iso_year: number; iso_week: number }>({
    mutationFn: ({ iso_year, iso_week }) => {
      const q = new URLSearchParams({
        iso_year: String(iso_year),
        iso_week: String(iso_week),
      });
      return apiFetch<DeleteWeekOut>(`/v1/admin/data/weekly?${q}`, { method: "DELETE" });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: QK.adminWeekly }),
  });
}
